import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { auth, googleProvider, storage, db } from '../firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import {
  createUserWithEmailAndPassword,
  onIdTokenChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  sendEmailVerification,
  signOut
} from 'firebase/auth';
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import API_BASE_URL from '../config/api';

const UserContext = createContext(null);

const authErrorMessages = {
  'auth/email-already-in-use': 'This email is already registered.',
  'auth/invalid-credential': 'Invalid email or password.',
  'auth/user-not-found': 'Account not found.',
  'auth/wrong-password': 'Invalid email or password.',
  'auth/too-many-requests': 'Too many attempts. Please try again later.'
};

const mapAuthError = (error) => {
  if (!error) return 'Something went wrong. Please try again.';
  if (error.message && !error.code) return error.message;
  return authErrorMessages[error.code] || 'Something went wrong. Please try again.';
};

const sanitizeUsername = (value = '') => value.toLowerCase().replace(/[^a-z0-9_]/g, '');

const getFileExtension = (file) => {
  const name = String(file?.name || '').trim();
  const idx = name.lastIndexOf('.');
  if (idx === -1) return '';
  const ext = name.slice(idx).toLowerCase();
  if (!ext || ext.length > 10) return '';
  return ext;
};

const buildUsernameCandidates = (user) => {
  const displayName = sanitizeUsername(user?.displayName || '');
  const emailSlug = sanitizeUsername(user?.email?.split('@')[0] || '');
  const base = displayName || emailSlug || 'hiker';
  const candidates = new Set([base]);
  while (candidates.size < 5) {
    candidates.add(`${base}${Math.floor(100 + Math.random() * 8999)}`);
  }
  return Array.from(candidates);
};

const fetchRemotePhotoFile = async (url) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('Failed to fetch remote profile photo.');
  }
  const contentType = response.headers.get('content-type') || 'image/jpeg';
  const extension = contentType.includes('png') ? 'png' : 'jpg';
  const blob = await response.blob();
  return new File([blob], `profile.${extension}`, { type: contentType });
};

export function UserProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const signupInFlightRef = useRef(false);

  const maybeSyncProfilePhoto = async ({ user, token, profile }) => {
    try {
      if (!user?.photoURL) return null;
      if (!profile?.username) return null;
      if (profile?.pfp && profile.pfp.trim() !== '') return null;

      const file = await fetchRemotePhotoFile(user.photoURL);
      const uid = auth.currentUser?.uid;
      if (!uid) return null;

      const extension = getFileExtension(file);
      const filename = `${Date.now()}_${Math.random().toString(36).slice(2, 9)}${extension}`;
      const objectPath = `pfp/uploads/${uid}/${filename}`;
      const fileRef = storageRef(storage, objectPath);
      await uploadBytes(fileRef, file, { contentType: file.type || undefined });
      const pfpUrl = await getDownloadURL(fileRef);

      const result = await updateUserPfp({ pfpUrl });
      if (result.success && result.data?.pfpUrl) {
        return result.data.pfpUrl;
      }
      return null;
    } catch (error) {
      console.warn('Profile photo sync failed:', error);
      return null;
    }
  };

  const fetchProfile = async (token) => {
    const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });

    if (response.status === 403) {
      return null;
    }

    const payload = await response.json().catch(() => ({}));
    if (!response.ok || !payload.success) {
      throw new Error(payload.message || 'Failed to fetch profile');
    }
    return payload.data;
  };

  const submitProfile = async ({ token, username, nickname, email, pfp }) => {
    const requestPayload = {
      username,
      nickname: (nickname || username).trim(),
      email: email ? email.trim() : ''
    };

    if (pfp) {
      const uid = auth.currentUser?.uid;
      if (!uid) {
        throw new Error('Not authenticated');
      }
      const extension = getFileExtension(pfp);
      const filename = `${Date.now()}_${Math.random().toString(36).slice(2, 9)}${extension}`;
      const objectPath = `pfp/uploads/${uid}/${filename}`;
      const fileRef = storageRef(storage, objectPath);
      await uploadBytes(fileRef, pfp, { contentType: pfp.type || undefined });
      requestPayload.pfpUrl = await getDownloadURL(fileRef);
    }

    const response = await fetch(`${API_BASE_URL}/api/auth/signup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(requestPayload)
    });

    const responsePayload = await response.json().catch(() => ({}));
    if (!response.ok || !responsePayload.success) {
      throw new Error(responsePayload.message || 'Failed to save profile');
    }
    return responsePayload.data;
  };

  const autoProvisionProfile = async (user, token) => {
    const candidates = buildUsernameCandidates(user);
    for (const username of candidates) {
      try {
        await submitProfile({
          token,
          username,
          nickname: user?.displayName || username,
          email: user?.email || ''
        });
        return await fetchProfile(token);
      } catch (error) {
        if (!/username already exists/i.test(error.message || '')) {
          throw error;
        }
      }
    }
    return null;
  };

  const ensureProfile = async (user, { allowAutoProvision = false } = {}) => {
    const token = await user.getIdToken();
    let profile = await fetchProfile(token);

    if (!profile && allowAutoProvision) {
      profile = await autoProvisionProfile(user, token);
    }

    if (!profile) {
      setCurrentUser(null);
      return null;
    }

    setCurrentUser({ ...profile, uid: user.uid, token, emailVerified: user.emailVerified });

    maybeSyncProfilePhoto({ user, token, profile }).then((syncedPhotoUrl) => {
      if (syncedPhotoUrl) {
        setCurrentUser((prev) => (prev ? { ...prev, pfp: syncedPhotoUrl } : prev));
      }
    });

    return profile;
  };

  const getAuthToken = async () => {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('Not authenticated');
    }
    const token = await user.getIdToken();
    setCurrentUser((prev) => (prev ? { ...prev, token } : prev));
    return token;
  };

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (user) => {
      setFirebaseUser(user);
      if (!user) {
        setCurrentUser(null);
        setInitializing(false);
        return;
      }

      if (signupInFlightRef.current) {
        return;
      }

      try {
        // If the local user object thinks they aren't verified yet, check the server
        if (!user.emailVerified) {
          try {
            await user.reload();
            if (user.emailVerified) {
              // Now that they are verified, force refresh the token to update claims
              await user.getIdToken(true);
            }
          } catch (e) {
            console.warn('Failed to reload user for verification check:', e);
          }
        }

        // Get custom claims from the ID token
        const idTokenResult = await user.getIdTokenResult();
        const claims = idTokenResult.claims || {};
        const allowAutoProvision = user.providerData?.some((provider) => provider.providerId === 'google.com');
        const profile = await ensureProfile(user, { allowAutoProvision });
        if (profile) {
          setCurrentUser((prev) => ({
            ...prev,
            admin: !!claims.admin, editor: !!claims.editor, emailVerified: user.emailVerified }));
        }
      } catch (error) {
        console.error('Error synchronizing user profile:', error);
      } finally {
        setInitializing(false);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // Listen to tab focus to refresh user status automatically (e.g. they verified in another tab)
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && auth.currentUser) {
        if (!auth.currentUser.emailVerified) {
          try {
            await auth.currentUser.reload();
            if (auth.currentUser.emailVerified) {
               // Token fetch will trigger onIdTokenChanged which sets everything up
               await auth.currentUser.getIdToken(true);
            }
          } catch (e) {
            console.warn('Visibility refresh failed:', e);
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const login = async (email, password) => {
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
      // After login, ensure profile exists
      const user = auth.currentUser;
      const profile = user ? await ensureProfile(user) : null;
      if (!profile) {
        await signOut(auth);
        return { success: false, message: 'This account has been deleted or disabled.' };
      }
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, message: mapAuthError(error) };
    }
  };

  const logout = async () => {
    await signOut(auth);
    setCurrentUser(null);
  };

  const signup = async ({ email, password, username, nickname, pfp }) => {
    try {
      signupInFlightRef.current = true;
      const credential = await createUserWithEmailAndPassword(auth, email.trim(), password);
      const token = await credential.user.getIdToken();
      await submitProfile({ token, username, nickname, email, pfp });
      await ensureProfile(credential.user);
      return { success: true };
    } catch (error) {
      console.error('Signup error:', error);
      return { success: false, message: mapAuthError(error) };
    } finally {
      signupInFlightRef.current = false;
      setInitializing(false);
    }
  };

  const loginWithGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      await ensureProfile(result.user, { allowAutoProvision: true });
      return { success: true };
    } catch (error) {
      console.error('Google login error:', error);
      return { success: false, message: mapAuthError(error) };
    }
  };

  const addToWishlist = async (mountainId) => {
    if (!currentUser) {
      console.log('User not logged in');
      return false;
    }

    try {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/users/wishlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ username: currentUser.username, mountainId })
      });
      const data = await response.json();
      if (data.success) {
        setCurrentUser((prev) => ({ ...prev, wishlist: data.data || [] }));
        return true;
      }
      console.error('Error updating wishlist:', data.message);
      return false;
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      return { success: false, message: 'Network error: ' + error.message };
    }
  };

  const markAsSummited = async (mountainId) => {
    if (!currentUser) {
      console.log('User not logged in');
      return false;
    }

    try {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/users/summited`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ username: currentUser.username, mountainId })
      });
      const data = await response.json();
      if (data.success) {
        setCurrentUser((prev) => ({ ...prev, summited: data.data || [] }));
        return true;
      }
      console.error('Error marking as summited:', data.message);
      return false;
    } catch (error) {
      console.error('Error marking as summited:', error);
      return { success: false, message: 'Network error: ' + error.message };
    }
  };

  const removeFromWishlist = async (mountainId) => {
    if (!currentUser) {
      console.log('User not logged in');
      return false;
    }

    try {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/users/wishlist/${mountainId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setCurrentUser((prev) => ({ ...prev, wishlist: data.data || [] }));
        return true;
      }
      console.error('Error removing from wishlist:', data.message);
      return false;
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      return { success: false, message: 'Network error: ' + error.message };
    }
  };

  const unmarkSummited = async (mountainId) => {
    if (!currentUser) {
      console.log('User not logged in');
      return false;
    }

    try {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/users/summited/${mountainId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setCurrentUser((prev) => ({ ...prev, summited: data.data || [] }));
        return true;
      }
      console.error('Error unmarking summited:', data.message);
      return false;
    } catch (error) {
      console.error('Error unmarking summited:', error);
      return { success: false, message: 'Network error: ' + error.message };
    }
  };

  const addToGearWatchlist = async (gearId) => {
    if (!currentUser) return { success: false, message: 'Not logged in' };
    try {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/users/gear-watchlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ gearId })
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        return { success: false, message: data.message || 'Failed to update watchlist' };
      }

      setCurrentUser((prev) => ({ ...prev, gearWatchlist: data.data || [] }));
      return { success: true, data: data.data || [] };
    } catch (error) {
      console.error('Error adding to gear watchlist:', error);
      return { success: false, message: 'Network error: ' + error.message };
    }
  };

  const removeFromGearWatchlist = async (gearId) => {
    if (!currentUser) return { success: false, message: 'Not logged in' };
    try {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/users/gear-watchlist/${encodeURIComponent(String(gearId || '').trim())}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        return { success: false, message: data.message || 'Failed to update watchlist' };
      }

      setCurrentUser((prev) => ({ ...prev, gearWatchlist: data.data || [] }));
      return { success: true, data: data.data || [] };
    } catch (error) {
      console.error('Error removing from gear watchlist:', error);
      return { success: false, message: 'Network error: ' + error.message };
    }
  };

  const updateGearWatchlistAlertFrequency = async (frequency) => {
    if (!currentUser) return { success: false, message: 'Not logged in' };
    try {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/users/gear-watchlist/alerts`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ frequency })
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        return { success: false, message: data.message || 'Failed to update watchlist alerts' };
      }

      setCurrentUser((prev) => ({
        ...prev,
        gearWatchlistAlertFrequency: data.data?.frequency || frequency
      }));

      return { success: true, data: data.data };
    } catch (error) {
      console.error('Error updating watchlist alert frequency:', error);
      return { success: false, message: 'Network error: ' + error.message };
    }
  };

  const updateUserPfp = async (payloadOrFormData) => {
    if (!currentUser) return { success: false, message: 'Not logged in' };
    try {
      const token = await getAuthToken();
      const isFormData = typeof FormData !== 'undefined' && payloadOrFormData instanceof FormData;
      const response = await fetch(`${API_BASE_URL}/api/users/${currentUser.username}/pfp`, {
        method: 'POST',
        headers: {
          ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
          Authorization: `Bearer ${token}`
        },
        body: isFormData ? payloadOrFormData : JSON.stringify(payloadOrFormData || {})
      });
      const data = await response.json();
      if (data.success) {
        setCurrentUser((prev) => ({ ...prev, pfp: data.data.pfpUrl }));
        return { success: true, data: data.data };
      }
      return { success: false, message: data.message };
    } catch (error) {
      console.error('Error updating profile picture:', error);
      return { success: false, message: 'Network error: ' + error.message };
    }
  };

  const updateProfile = async ({ nickname, bio, website, instagram, youtube, email }) => {
    if (!currentUser) return { success: false, message: 'Not logged in' };
    try {
      const socials = [
        { type: 'website', url: (website || '').trim() },
        { type: 'youtube', url: (youtube || '').trim() },
        { type: 'instagram', handle: (instagram || '').trim().replace(/^@/, '') }
      ].filter((s) => {
        if (s.type === 'instagram') return (s.handle || '').trim() !== '';
        return (s.url || '').trim() !== '';
      });

      const token = await getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/users/${currentUser.username}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ nickname, bio, email, socials })
      });
      const data = await response.json();
      if (data.success) {
        setCurrentUser((prev) => ({
          ...prev,
          ...data.data,
          socials: data.data?.socials ?? socials
        }));
        return { success: true, data: data.data };
      }
      return { success: false, message: data.message };
    } catch (error) {
      console.error('Error updating profile:', error);
      return { success: false, message: 'Network error: ' + error.message };
    }
  };

  const resendVerificationEmail = async () => {
    try {
      const user = auth.currentUser;
      if (user) {
        const token = await getAuthToken();
        const response = await fetch(`${API_BASE_URL}/api/auth/resend-verification`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await response.json();
        if (data.success) {
          return { success: true, message: 'Verification email sent' };
        } else {
          return { success: false, message: data.message };
        }
      }
      return { success: false, message: 'No registered user' };
    } catch (error) {
      console.error('Error resending email:', error);
      return { success: false, message: error.message };
    }
  };

  const applyForEditorial = async () => {
    if (!currentUser) return { success: false, message: 'Not logged in' };
    try {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/users/me/editorial-application`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.success) {
        if (data.data) {
          setCurrentUser((prev) => ({ ...prev, ...data.data }));
        }
        return { success: true, data: data.data };
      }
      return { success: false, message: data.message || `Request failed (${response.status})` };
    } catch (error) {
      console.error('Apply for editorial error:', error);
      return { success: false, message: 'Network error: ' + error.message };
    }
  };

  const followUser = async (targetUsername) => {
    if (!currentUser) return { success: false, message: 'Not logged in' };
    try {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/users/${targetUsername}/follow`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        // Update currentUser state with new following list
        setCurrentUser(prev => ({ ...prev, following: data.following }));
      }
      return data;
    } catch (error) {
       console.error('Follow error:', error);
       return { success: false, message: error.message };
    }
  };

  const unfollowUser = async (targetUsername) => {
    if (!currentUser) return { success: false, message: 'Not logged in' };
    try {
      const token = await getAuthToken();
       const response = await fetch(`${API_BASE_URL}/api/users/${targetUsername}/follow`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setCurrentUser(prev => ({ ...prev, following: data.following }));
      }
      return data;
    } catch (error) {
       console.error('Unfollow error:', error);
       return { success: false, message: error.message };
    }
  };

  const value = {
    currentUser,
    firebaseUser,
    initializing,
    login,
    logout,
    signup,
    loginWithGoogle,
    addToWishlist,
    addToGearWatchlist,
    markAsSummited,
    removeFromWishlist,
    removeFromGearWatchlist,
    unmarkSummited,
    updateUserPfp,
    updateProfile,
    resendVerificationEmail,
    updateGearWatchlistAlertFrequency,
    applyForEditorial,
    followUser,
    unfollowUser
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}
